'use client';
import ProductCardPrimary2 from "@/components/shared/cards/ProductCardPrimary2";
import Nodata from "@/components/shared/no-data/Nodata";
import ShopDataShowing from "@/components/shared/products/ShopDataShowing";
import ShopSortSelect from "@/components/shared/products/ShopSortSelect";
import ProductSidebar from "@/components/shared/sidebars/ProductSidebar";
import {useEffect, useState, useTransition} from 'react';
import CatalogFilterButton from '@/components/shared/products/CatalogFilterButton';
import {usePaginatedQuery} from '@/libs/api/baseRequests';
import {GET_PRODUCTS_QUERY} from '@/graphql/queries';
import {useLocale, useTranslations} from 'next-intl';
import {CatalogFilterSessionStorage, CatalogSortSelectItems, CURRENCY_DEFAULT, ProductsPaginatedTake} from '@/libs/constants/common.constants';
// todo check react-window-infinite-loader if there are a lot of items instead of below module
import InfiniteScroll from 'react-infinite-scroll-component';
import {useSearchParams} from 'next/navigation';
import {pricesItems} from '@/components/shared/sidebars/widgets/PriceRange';
import PriceRangeInput from '@/components/shared/sidebars/widgets/PriceRangeInput';
import CategoriesMenu from '@/components/layout/headers/CategoriesMenu';
import ProductCardPrimary from '@/components/shared/cards/ProductCardPrimary';
import ProductSkeletonList from '@/components/sections/products/skeleton/ProductSkeletonList';
import {parseProductItems} from '@/libs/api/fetch/fetchCallbacks';
import {generateStars} from '@/libs/rating';
import {
  addItemsToSessionStorage,
  addToSessionStorage,
  deleteFromSessionStorage,
  getFromSessionStorage,
  getItemsFromSessionStorage
} from '@/libs/session-storage.util';
import {useUserContext} from '@/providers/UserContext';

export default function ProductsPrimary({ home, socialLinks, products, currency, options, categories, queryParams, brands, setHeroTitle, isMobile }) {
  const locale = useLocale();
  const t = useTranslations('catalog');
  const isGrid = true; // false -> col
  
  const {user} = useUserContext();
  
  const query = useSearchParams();
  const searchQuery = query?.get('search')?.toString();
  const categoryQuery = query?.get('category')?.toString();
  const subcategoryQuery = query?.get('subcategory')?.toString();
  const brandQuery = query?.get('brand')?.toString();
  const tagQuery = query?.get('tag')?.toUpperCase();
  
  const [isFetching, startFetching] = useTransition();
  const [isInit, setInit] = useState(false);
  const [currencyTitle, setCurrencyTitle] = useState(currency?.title ?? CURRENCY_DEFAULT);
  const [priceRanges, setPriceRanges] = useState(pricesItems(currency?.title));
  
  const [sortSelect, setSortSelect] = useState({
    value: { fieldName: 'reviewComputed.average', direction: 'DESC', nulls: 'LAST' },
    ref: CatalogSortSelectItems.DEFAULT
  });
  const [searchInput, setSearchInput] = useState(searchQuery);
  const [priceFromInput, setPriceFromInput] = useState();
  const [priceToInput, setPriceToInput] = useState();
  const [categoryInput, setCategoryInput] = useState(categoryQuery);
  const [subcategoryInput, setSubcategoryInput] = useState(subcategoryQuery);
  const [brandInput, setBrandInput] = useState(brandQuery);
  const [tagInput, setTagInput] = useState(tagQuery);
  const [filters, setFilters] = useState(queryParams);
  
  const { items, fetchMoreData, hasMore, refetchData, total } = usePaginatedQuery(
      GET_PRODUCTS_QUERY, {
        locale,
        take: ProductsPaginatedTake,
        skip: 0,
        currency: currencyTitle
      },
      {...options, skipFirst: true},
      products
  );
  
  // todo add ability to add blogs to catalog like: 6-9 products, 1 blog, etc
  useEffect(() => {
    if (!isFetching) {
      const sortFilter = getItemsFromSessionStorage(CatalogFilterSessionStorage.SORT) || undefined;
      const searchFilter = getFromSessionStorage(CatalogFilterSessionStorage.SEARCH) || undefined;
      const priceFromFilter = getItemsFromSessionStorage(CatalogFilterSessionStorage.PRICE_FROM) || undefined;
      const priceToFilter = getItemsFromSessionStorage(CatalogFilterSessionStorage.PRICE_TO) || undefined;
      const categoryFilter = getFromSessionStorage(CatalogFilterSessionStorage.CATEGORY) || undefined;
      const subcategoryFilter = getFromSessionStorage(CatalogFilterSessionStorage.SUBCATEGORY) || undefined;
      const brandFilter = getFromSessionStorage(CatalogFilterSessionStorage.BRAND) || undefined;
      const tagFilter = getFromSessionStorage(CatalogFilterSessionStorage.TAG) || undefined;
      
      let hero = null;
      const filters = [];
      if (sortFilter) setSortSelect(sortFilter);
      if (priceFromFilter) setPriceFromInput(priceFromFilter);
      if (priceToFilter) setPriceToInput(priceToFilter);
      if (categoryFilter) {
        setCategoryInput(categoryFilter);
        hero = { queryParam: 'category', title: categoryFilter };
        filters.push(hero);
      }
      if (subcategoryFilter) {
        setSubcategoryInput(subcategoryFilter);
        hero = { queryParam: 'category', title: subcategoryFilter };
        filters.push(hero);
      }
      if (brandFilter) {
        setBrandInput(brandFilter);
        hero = { queryParam: 'brand', title: brandFilter };
        filters.push(hero);
      }
      if (tagFilter) {
        setTagInput(tagFilter);
        hero = { queryParam: 'tag', title: t(`productSectionTags.${tagFilter}`) };
        filters.push(hero);
      }
      if (searchFilter) {
        setSearchInput(searchFilter);
        hero = { queryParam: 'search', title: searchFilter };
        filters.push(hero);
      }
      
      if (hero) {
        setHeroTitle(hero);
        setFilters(filters);
      }
      
      startFetching(async () => {
        await refetchData({
          currency: currency?.title ?? CURRENCY_DEFAULT,
          search: searchQuery || searchFilter,
          orderBy: sortFilter ? [sortFilter.value] : sortSelect.value,
          byPriceFrom: priceFromFilter?.value,
          byPriceTo: priceToFilter?.value,
          category: categoryQuery || categoryFilter,
          subcategory: subcategoryQuery || subcategoryFilter,
          brand: brandQuery || brandFilter,
          tag: tagQuery || tagFilter
        }, (items) => {
          parseProductItems(items, (item) => {
            if (item.reviewComputed) {
              item.reviewComputed.stars = generateStars(item.reviewComputed.average);
            }
          });
        });
      });
    }
    
  }, []);
  
  useEffect(() => {
    if (currencyTitle !== (currency?.title ?? CURRENCY_DEFAULT) && !isFetching) {
      startFetching(async () => {
        setPriceRanges(pricesItems(currency?.title));
        setCurrencyTitle(currency?.title ?? CURRENCY_DEFAULT);
        await refetchData({
          currency: currency?.title ?? CURRENCY_DEFAULT,
          search: searchInput,
          orderBy: [sortSelect.value],
          category: categoryInput,
          subcategory: subcategoryInput,
          brand: brandInput,
          tag: tagInput
        }, (items) => {
          parseProductItems(items, (item) => {
            if (item.reviewComputed) {
              item.reviewComputed.stars = generateStars(item.reviewComputed.average);
            }
          });
        });
      });
    }
  }, [currency]);
  
  useEffect(() => {
    if (isInit && !isFetching) {
      startFetching(async () => {
        await refetchData({
          currency: currency?.title ?? CURRENCY_DEFAULT,
          search: searchInput,
          orderBy: [sortSelect.value],
          byPriceFrom: priceFromInput?.value,
          byPriceTo: priceToInput?.value,
          category: categoryInput,
          subcategory: subcategoryInput,
          brand: brandInput,
          tag: tagInput
        }, (items) => {
          parseProductItems(items, (item) => {
            if (item.reviewComputed) {
              item.reviewComputed.stars = generateStars(item.reviewComputed.average);
            }
          });
        });
      });
    }
    setInit(true);
  }, [sortSelect, searchInput, priceFromInput, priceToInput, categoryInput, subcategoryInput, brandInput, tagInput]);
  
  const onSortSelect = (value) => {
    setSortSelect(value);
    addItemsToSessionStorage(CatalogFilterSessionStorage.SORT, value);
  }
  
  const onSearch = (e) => {
    e.preventDefault();
    const val = e.target[0]?.value?.trim();
    setSearchInput(val);
    if (val?.length) {
      setHeroTitle({ queryParam: 'search', title: val });
      addToSessionStorage(CatalogFilterSessionStorage.SEARCH, val);
    } else {
      setHeroTitle(null);
      deleteFromSessionStorage(CatalogFilterSessionStorage.SEARCH);
    }
    upsertFilters(val?.length, 'search', val);
  }
  
  const onPriceChange = (item, type, otherValue) => {
    if (type === 'from' && (otherValue == null || item.value <= otherValue || item.value === null)) {
      setPriceFromInput(item);
      addItemsToSessionStorage(CatalogFilterSessionStorage.PRICE_FROM, item);
    } else if (type === 'to' && (otherValue == null || item.value >= otherValue || item.value === null)) {
      setPriceToInput(item);
      addItemsToSessionStorage(CatalogFilterSessionStorage.PRICE_TO, item);
    }
  }
  
  const onPriceRemove = (type) => {
    if (type === 'from') {
      setPriceFromInput(null);
      deleteFromSessionStorage(CatalogFilterSessionStorage.PRICE_FROM);
    } else if (type === 'to') {
      setPriceToInput(null);
      deleteFromSessionStorage(CatalogFilterSessionStorage.PRICE_TO);
    }
  }
  
  const onCategorySelect = (e, alt, title, isActive) => {
    e.preventDefault();
    
    setSubcategoryInput(null);
    deleteFromSessionStorage(CatalogFilterSessionStorage.SUBCATEGORY);
    if (isActive) {
      setCategoryInput(null);
      deleteFromSessionStorage(CatalogFilterSessionStorage.CATEGORY);
      setHeroTitle(null);
    } else {
      setCategoryInput(alt);
      addToSessionStorage(CatalogFilterSessionStorage.CATEGORY, alt);
      setHeroTitle({ queryParam: 'category', title });
    }
    upsertFilters(!isActive, 'category', title);
  }
  
  const onSubcategorySelect = (e, alt, title, isActive) => {
    e.preventDefault();
    
    setCategoryInput(null);
    deleteFromSessionStorage(CatalogFilterSessionStorage.CATEGORY);
    if (isActive) {
      setSubcategoryInput(null);
      deleteFromSessionStorage(CatalogFilterSessionStorage.SUBCATEGORY);
      setHeroTitle(null);
    } else {
      setSubcategoryInput(alt);
      addToSessionStorage(CatalogFilterSessionStorage.SUBCATEGORY, alt);
      setHeroTitle({ queryParam: 'category', title });
    }
    upsertFilters(!isActive, 'category', title);
  }
  
  const onBrandSelect = (e, alt, title, isActive) => {
    e.preventDefault();
    
    if (isActive) {
      setBrandInput(null);
      deleteFromSessionStorage(CatalogFilterSessionStorage.BRAND);
      setHeroTitle(null);
    } else {
      setBrandInput(alt);
      addToSessionStorage(CatalogFilterSessionStorage.BRAND, alt);
      setHeroTitle({ queryParam: 'brand', title });
    }
    upsertFilters(!isActive, 'brand', title);
  }
  
  const onTagSelect = (e, title, isActive) => {
    e.preventDefault();
    
    if (isActive) {
      setTagInput(null);
      deleteFromSessionStorage(CatalogFilterSessionStorage.TAG);
      setHeroTitle(null);
    } else {
      setTagInput(title);
      addToSessionStorage(CatalogFilterSessionStorage.TAG, title);
      setHeroTitle({ queryParam: 'tag', title: t(`productSectionTags.${title}`) });
    }
    upsertFilters(!isActive, 'tag', t(`productSectionTags.${title}`));
  }
  
  const onResetFilters = () => {
    if (sortSelect.ref !== CatalogSortSelectItems.DEFAULT) {
      deleteFromSessionStorage(CatalogFilterSessionStorage.SORT);
      setSortSelect({
        value: { fieldName: 'reviewComputed.average', direction: 'DESC', nulls: 'LAST' },
        ref: CatalogSortSelectItems.DEFAULT
      });
    }
    if (!!searchInput) {
      deleteFromSessionStorage(CatalogFilterSessionStorage.SEARCH);
      setSearchInput(null);
    }
    if (!!priceFromInput) {
      deleteFromSessionStorage(CatalogFilterSessionStorage.PRICE_FROM);
      setPriceFromInput(null);
    }
    if (!!priceToInput) {
      deleteFromSessionStorage(CatalogFilterSessionStorage.PRICE_TO);
      setPriceToInput(null);
    }
    if (!!categoryInput) {
      deleteFromSessionStorage(CatalogFilterSessionStorage.CATEGORY);
      setCategoryInput(null);
    }
    if (!!subcategoryInput) {
      deleteFromSessionStorage(CatalogFilterSessionStorage.SUBCATEGORY);
      setSubcategoryInput(null);
    }
    if (!!brandInput) {
      deleteFromSessionStorage(CatalogFilterSessionStorage.BRAND);
      setBrandInput(null);
    }
    if (!!tagInput) {
      deleteFromSessionStorage(CatalogFilterSessionStorage.TAG);
      setTagInput(null);
    }
    if (filters?.length > 0) {
      setFilters([]);
    }
    setHeroTitle(t('catalog'));
  }
  
  const upsertFilters = (isActive, queryParam, title) => {
    setFilters((prev) => {
      const updated = [...prev];
      const index = updated.findIndex(it => it.queryParam === queryParam);
      
      if (isActive) {
        if (index !== -1) {
          updated[index] = { queryParam, title };
        } else {
          updated.push({ queryParam, title });
        }
      } else if (index !== -1) {
        updated.splice(index, 1);
      }
      
      return updated;
    });
  }
  
  return (
      <>
        <div className="ltn__product-area ltn__product-gutter mb-120">
          <div className="container">
            <div className="row">
              <div className="col-lg-8 order-lg-2">
                <div className="catalog-list-filter-button d-lg-none">
                  <div>
                    <div className="filter-count">
                      {
                        t('countOfLength', {
                          count: items.length,
                          length: total
                        })
                      }
                    </div>
                    {
                      filters?.length ? (
                          <div>
                            {
                              filters.map((item, i) => (
                                  <span key={i} className="me-2">{
                                    t.rich('filterIs', {
                                      b: (chunks) => (<b className={i !== 0 ? 'text-lowercase' : ''}>{chunks}</b>),
                                      filter: t(item.queryParam),
                                      title: item.title
                                    })
                                  }{i !== filters.length - 1 ? ';' : ''}</span>
                              ))
                            }
                          </div>
                      ) : ''
                    }
                  </div>
                  <span className="filter-btn">
                  <span className="me-2">{t('filter')}</span>
                  <i className="fas fa-filter"></i>
                </span>
                </div>
                <div className={`catalog-list-header ${filters?.length ? 'sticky-header-query' : ''}`}>
                  <div className={`ltn__shop-options`}>
                    <ul>
                      <li className="d-none d-lg-block">
                        <ShopDataShowing
                            showingCount={items.length}
                            totalLength={total}
                        />
                      </li>
                      <li>
                        <ShopSortSelect
                            onSortSelect={onSortSelect}
                            sortValue={sortSelect.ref}
                        />
                      </li>
                      <li className="d-lg-none">
                        <CatalogFilterButton/>
                      </li>
                    </ul>
                  </div>
                  <div className={`catalog-filter-header d-lg-none row`}>
                    <div className="col-md-6">
                      <form
                          className="search-catalog"
                          onSubmit={onSearch}
                      >
                        <input
                            type="text"
                            name="search"
                            placeholder={t('searchYourKeyword')}
                            defaultValue={searchInput}
                        />
                        <button type="submit" className="theme-btn-1 btn-effect-1">
                          <i className="fas fa-search"></i>
                        </button>
                        {/*<QuickSearchItems />*/}
                      </form>
                    </div>
                    <div className="col-md-6">
                      <div className="price-filter-catalog">
                        <PriceRangeInput
                            currency={currency}
                            priceFromInput={priceFromInput}
                            priceToInput={priceToInput}
                            priceRanges={priceRanges}
                            onPriceRemove={onPriceRemove}
                            onPriceChange={onPriceChange}
                        />
                      </div>
                    </div>
                    <div className="reset-filter">
                      <button
                          onClick={onResetFilters}
                          title={t('resetFilters')}
                          disabled={isFetching}
                      >
                        <span>{t('resetFilters')}</span>
                      </button>
                    </div>
                  </div>
                </div>
                {!total ? <Nodata text={t('nothingFound')}/> : ''}
                <div className="tab-content">
                  <div className="tab-pane fade active">
                    <div className={`ltn__product-tab-content-inner ${isGrid ? 'ltn__product-grid-view' : 'ltn__product-list-view'}`}>
                      {/* <!-- ltn__product-item --> */}
                      {
                        isFetching ? (
                            <div className="row g-4">
                              <ProductSkeletonList isGrid={isGrid}/>
                            </div>
                        ) : (
                            <InfiniteScroll
                                dataLength={items.length}
                                next={() => fetchMoreData({
                                  orderBy: [sortSelect.value],
                                  search: searchInput,
                                  byPriceFrom: priceFromInput?.value,
                                  byPriceTo: priceToInput?.value,
                                  category: categoryInput,
                                  subcategory: subcategoryInput,
                                  brand: brandInput,
                                  tag: tagInput
                                }, (items) => {
                                  parseProductItems(items, (item) => {
                                    if (item.reviewComputed) {
                                      item.reviewComputed.stars = generateStars(item.reviewComputed.average);
                                    }
                                  });
                                })}
                                hasMore={hasMore}
                                style={{overflowX: 'hidden'}}
                                scrollThreshold={0.7}
                                loader={
                                  <div className="row g-4">
                                    <ProductSkeletonList isGrid={isGrid}/>
                                  </div>
                                }
                            >
                              <div className="row">
                                {
                                  isGrid ? (
                                      items?.map((product, idx) => (
                                          <div className="col-xl-4 col-sm-6 col-6" key={idx}>
                                            <ProductCardPrimary
                                                product={product}
                                                isMobile={isMobile}
                                                clientType={user?.clientType}
                                            />
                                          </div>
                                      ))
                                  ) : (
                                      items?.map((product, idx) => (
                                          <div className="col-lg-12" key={idx}>
                                            <ProductCardPrimary2
                                                product={product}
                                                clientType={user?.clientType}
                                            />
                                          </div>
                                      ))
                                  )
                                }
                              </div>
                            </InfiniteScroll>
                        )
                      }
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-lg-4 d-none d-lg-block">
                <ProductSidebar
                    onSearch={onSearch}
                    searchInput={searchInput}
                    currency={currency}
                    priceFromInput={priceFromInput}
                    priceToInput={priceToInput}
                    priceRanges={priceRanges}
                    onPriceRemove={onPriceRemove}
                    onPriceChange={onPriceChange}
                    categories={categories}
                    brands={brands}
                    onCategorySelect={onCategorySelect}
                    onSubcategorySelect={onSubcategorySelect}
                    categoryInput={categoryInput}
                    subcategoryInput={subcategoryInput}
                    onBrandSelect={onBrandSelect}
                    brandInput={brandInput}
                    onTagSelect={onTagSelect}
                    tagInput={tagInput}
                    resetFilters={onResetFilters}
                    isFetching={isFetching}
                />
              </div>
            </div>
          </div>
        </div>
        <CategoriesMenu
            home={home}
            socialLinks={socialLinks}
            categories={categories}
            brands={brands}
            isCatalog
            onCategorySelect={onCategorySelect}
            onSubcategorySelect={onSubcategorySelect}
            categoryInput={categoryInput}
            subcategoryInput={subcategoryInput}
            onBrandSelect={onBrandSelect}
            brandInput={brandInput}
            onTagSelect={onTagSelect}
            tagInput={tagInput}
        />
      </>
  );
};
